import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Link, useHistory } from "react-router-dom";
import { Box, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { AutomationConfigTemplateHeader } from "../CapAutoComponents";
import { Cap2BetaContent } from "./Cap2BetaContent";
import { Cap2ViewToggle } from "./Cap2ViewToggle";
import { useLlmScreeningGate } from "src/helpers/licensedRolloutGates";

/**
 * Standalone route for the Advanced CAP Screening (BETA) view. The same
 * content is reachable in place via the v2 toggle on the Audit History
 * page; this route keeps the view deep-linkable.
 */
export const AdvancedCapScreeningBeta = (props) => {
  const templateId = props?.match?.params?.automation_config_template_id;
  const history = useHistory();
  const productionUrl = `/products/cap-ai/auto/act/${templateId}/automation-records`;
  // License + rollout gate: this route is deep-linkable, so it must enforce
  // the cap-llm-screening license and flag itself — a saved link lands on
  // the production view instead when the gate is closed. The redirect waits
  // for both the license query and PostHog to answer: the hook is
  // fail-closed while they load, so acting on the initial `false` would
  // bounce accounts that HAVE the feature on every cold page load (review
  // finding on #2547).
  const { enabled: llmScreeningEnabled, loaded } = useLlmScreeningGate();

  useEffect(() => {
    if (loaded && !llmScreeningEnabled) history.replace(productionUrl);
  }, [loaded, llmScreeningEnabled, history, productionUrl]);

  if (!llmScreeningEnabled) return null;

  return (
    <div style={{ padding: "24px" }} data-testid="cap2-beta-container">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <AutomationConfigTemplateHeader
          title="Advanced CAP Screening (Beta)"
          automationConfigTemplateId={templateId}
          testIdPrefix="cap2-beta"
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Cap2ViewToggle
            checked
            onChange={() => history.push(productionUrl)}
          />
          <Button
            component={Link}
            to={productionUrl}
            startIcon={<ArrowBackIcon />}
            size="small"
            data-testid="cap2-beta-return"
          >
            Return to production screening
          </Button>
        </Box>
      </Box>
      <Cap2BetaContent templateId={templateId} />
    </div>
  );
};

AdvancedCapScreeningBeta.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      automation_config_template_id: PropTypes.string,
    }),
  }),
};

export default AdvancedCapScreeningBeta;
